import createMessagesMiddleware from "./messages";
import createMetricsMiddleware from "./metrics";
import createAsyncMiddleware from "./async";
/**
 * js-auth-tools uses several middlewares, such as asyncMiddleware, metricsMiddleware, and messagesMiddleware.
 *
 * Instead of importing & configuring each middleware separately, it's strongly recommended that
 * you use `createAuthToolsMiddlewares` to configure and return the array of needed middlewares.
 *
 * @param {object} options
 * @param {object} options.asyncOptions - Required. Same options as `createAsyncMiddleware`.
 * @param {object} [options.metricsOptions] - Optional but strongly recommended. Same options as `createMetricsMiddleware`.
 * @param {object} [options.messagesOptions] - Optional
 * @param {function} [options.messagesOptions.successAction] see `createMessagesMiddleware`
 * @param {function} [options.messagesOptions.errorAction] see `createMessagesMiddleware`
 *
 * @return {array} Array of middlewares.
 *                 Always includes asyncMiddleware, but will include more depending on the respective option parameters.
 */

export var createAuthToolsMiddlewares = function createAuthToolsMiddlewares(_ref) {
  var asyncOptions = _ref.asyncOptions,
      metricsOptions = _ref.metricsOptions,
      messagesOptions = _ref.messagesOptions;
  var middlewares = [createAsyncMiddleware(asyncOptions)];

  if (metricsOptions) {
    middlewares.push(createMetricsMiddleware(metricsOptions));
  }

  if (messagesOptions) {
    var successAction = messagesOptions.successAction,
        errorAction = messagesOptions.errorAction;
    middlewares.push(createMessagesMiddleware(successAction, errorAction));
  }

  return middlewares;
}; // Used directly by some apps

export { default as createAsyncMiddleware } from "./async"; // Only used in mobile-creditcards

export { getErrorMessages, getSuccessMessages } from "./messages"; // Account linking middlewares

export { default as providerDetailsMiddleware } from "./financial-accounts/provider-details";
export { default as createProviderAccountStateMiddleware } from "./financial-accounts/provider-account-status";
export { default as createProviderAccountLinkingMiddleware } from "./financial-accounts/provider-account-linking";
export { default as providerAccountsMiddleware } from "./financial-accounts/provider-accounts";
export { default as createProviderAccountsRefreshMiddleware } from "./financial-accounts/provider-accounts-refresh";
export { default as transactionsPaginationMiddleware } from "./financial-accounts/transactions-pagination";
export { default as transactionsBulkMiddleware } from "./financial-accounts/transactions-bulk";
export { default as transactionMonthlyRollupsMiddleware } from "./financial-accounts/transaction-monthly-rollups";
export { default as transactionsRecategorizationMiddleware } from "./financial-accounts/transactions-recategorization";
export { default as transactionsTopSpendingMiddleware } from "./financial-accounts/transactions-top-spending";
export { default as transactionRollupsMiddleware } from "./financial-accounts/transaction-rollups";
export { default as transactionsResyncMiddleware } from "./financial-accounts/transactions-resync"; // Redux Saga middlewares

export { default as createMetricsSaga } from "./metrics/index-saga";
export { default as featureActivationLocalStorageSaga } from "./feature-activation-local-storage/index-saga"; // Note: Do not export createFlowsSaga here because its dependencies import
// Web-only files (*.module.less) that mobile-creditcard's build process can't handle
// export { default as createFlowsSaga } from './flows/sagas/index-saga';